import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getVerificationStatus } from "@/lib/verification-check.functions";

const statusQuery = {
  queryKey: ["verification-status"],
  queryFn: () => getVerificationStatus(),
  staleTime: 30_000,
};

export const Route = createFileRoute("/verification-status")({
  head: () => ({
    meta: [
      { title: "Verification Status — Vending Machine Free.com" },
      { name: "robots", content: "noindex,nofollow,noarchive" },
      { name: "googlebot", content: "noindex,nofollow" },
      {
        name: "description",
        content: "Live status of site-root verification files (Google, Bing, etc.).",
      },
    ],
  }),
  headers: () => ({
    "X-Robots-Tag": "noindex, nofollow, noarchive",
    "Cache-Control": "no-store",
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(statusQuery),
  component: VerificationStatusPage,
});

function VerificationStatusPage() {
  const router = useRouter();
  const { data } = useSuspenseQuery(statusQuery);

  const allOk = data.results.every((r) => r.ok);
  const checked = new Date(data.checkedAt).toLocaleString();

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Verification Status</h1>
          <p className="mt-1 text-sm text-slate-600">
            Live checks against <code>{data.baseUrl}</code>. Page is <code>noindex</code>.
          </p>
        </div>
        <button
          onClick={() => router.invalidate()}
          className="rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-700"
        >
          Re-check now
        </button>
      </div>

      <div
        className={`mb-4 rounded-md border px-4 py-3 text-sm ${
          allOk
            ? "border-green-300 bg-green-50 text-green-900"
            : "border-red-300 bg-red-50 text-red-900"
        }`}
      >
        <strong>{allOk ? "✅ All files reachable" : "❌ One or more files failing"}</strong>
        <span className="ml-2 text-xs opacity-70">Last checked: {checked}</span>
        {data.lastSuccessAt && (
          <div className="mt-1 text-xs opacity-80">
            Last successful check: {new Date(data.lastSuccessAt).toLocaleString()}
          </div>
        )}
      </div>

      <div
        className={`mb-6 rounded-md border px-4 py-3 text-sm ${
          data.noindex.ok
            ? "border-green-300 bg-green-50 text-green-900"
            : "border-amber-300 bg-amber-50 text-amber-900"
        }`}
      >
        <strong>
          {data.noindex.ok
            ? "✅ /verification-status is noindex"
            : "⚠️ /verification-status noindex check failed"}
        </strong>
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
          <dt>URL</dt>
          <dd className="font-mono break-all">{data.noindex.url}</dd>
          <dt>HTTP</dt>
          <dd>{data.noindex.status ?? "—"}</dd>
          <dt>
            <code>&lt;meta name="robots"&gt;</code>
          </dt>
          <dd className="font-mono">{data.noindex.metaRobots ?? "— (none)"}</dd>
          <dt>
            <code>X-Robots-Tag</code>
          </dt>
          <dd className="font-mono">{data.noindex.xRobotsTag ?? "— (none)"}</dd>
          {data.noindex.error && (
            <>
              <dt className="text-amber-900">Issue</dt>
              <dd className="text-amber-900">{data.noindex.error}</dd>
            </>
          )}
        </dl>
      </div>


      <ul className="space-y-3">
        {data.results.map((r) => (
          <li
            key={r.filename}
            className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between gap-3">
              <a
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-sm text-blue-700 underline"
              >
                {r.filename}
              </a>
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold ${
                  r.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}
              >
                {r.ok ? "OK" : "FAIL"}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
              <dt>HTTP status</dt>
              <dd>{r.status ?? "—"}</dd>
              <dt>Content-Type</dt>
              <dd>{r.contentType ?? "—"}</dd>
              <dt>Checked at</dt>
              <dd>{new Date(r.checkedAt).toLocaleString()}</dd>
              {r.error && (
                <>
                  <dt className="text-red-700">Error</dt>
                  <dd className="text-red-700">{r.error}</dd>
                </>
              )}
            </dl>
          </li>
        ))}
        {data.results.length === 0 && (
          <li className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No verification files registered in
            <code className="mx-1">scripts/verification-files.config.json</code>.
          </li>
        )}
      </ul>

      <p className="mt-8 text-xs text-slate-500">
        Add new files to <code>public/</code> and register them in
        <code className="mx-1">scripts/verification-files.config.json</code>. Webhook
        alerts (Slack/Discord/email) fire on CI failures — see
        <code className="mx-1">public/README-verification.md</code>.
      </p>
    </main>
  );
}
